import { useContext, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { haveValue, toast, getUserDisplayName } from "../../helper/common";
import useTranslator from "../../hooks/useTranslator";
import SkorProtectBadge from "../SkorProtectBadge/SkorProtectBadge";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {
  updateTenantShareRequest,
  getTenantShareRequests,
} from "../../services/sharedService";
import LandLordContext from "../../context/landlord/LandLordContext";

const getInitials = (name = "") => {
  const parts = String(name || "")
    .trim()
    .split(" ")
    .filter(Boolean);

  if (!parts.length) return "--";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

const formatIncome = (value, isAnnual = false) => {
  if (!haveValue(value) || Number.isNaN(Number(value))) return "-";
  const monthlyValue = isAnnual ? Number(value) / 12 : Number(value);
  if (monthlyValue === 0) {
    return null;
  }
  return `${monthlyValue.toLocaleString("es-ES")}€/mo`;
};

const normalizeApplications = (raw = [], _t) => {
  if (!Array.isArray(raw)) return [];

  return raw.map((request, index) => {
    const tenant = request?.tenant_id;
    const application = request?.application_id;
    const isApplication = !!application;

    const tenantFullName =
      `${tenant?.fname || ""} ${tenant?.lname || ""}`.trim();
    const tenantInitials = getInitials(tenantFullName);

    const candidates = [];

    if (!isApplication) {
      const guarantors = (tenant?.gurantor_id || []).map((g, gIndex) => {
        const gName = getUserDisplayName(g);
        return {
          id: g?._id || `guarantor-${index}-${gIndex}`,
          fullName: gName || `Guarantor ${gIndex + 1}`,
          initials: getInitials(gName),
          income: g?.current_annual_income,
          isAnnualIncome: true,
        };
      });

      candidates.push({
        id: tenant?._id || `tenant-${index}`,
        fullName: tenantFullName || "Inquilino",
        initials: tenantInitials,
        income: tenant?.current_annual_income,
        isAnnualIncome: true,
        guarantors,
        email: tenant?.email,
        skorProtect: tenant?.skor_protect || undefined,
      });
    } else {
      const participants = application?.participants || [];
      participants.forEach((participant, participantIndex) => {
        const tenantInfo = participant?.tenant_info;
        const participantName = tenantInfo
          ? getUserDisplayName(tenantInfo)
          : participant?.name || `Participant ${participantIndex + 1}`;
        const isMainTenant = participant?.id === tenant?._id;

        const guarantors = (tenantInfo?.gurantor_id || []).map((g, gIndex) => {
          const gName = getUserDisplayName(g);
          return {
            id: g?._id || `guarantor-${index}-${participantIndex}-${gIndex}`,
            fullName: gName || `Guarantor ${gIndex + 1}`,
            initials: getInitials(gName),
            income: g?.current_annual_income,
            isAnnualIncome: true,
          };
        });

        candidates.push({
          id: participant?.id || `participant-${index}-${participantIndex}`,
          fullName: participantName,
          initials: getInitials(participantName),
          income: tenantInfo
            ? tenantInfo?.current_annual_income
            : isMainTenant
              ? tenant?.current_annual_income
              : 0,
          isAnnualIncome: false,
          guarantors,
          email: participant?.email,
          consentStatus:
            participant?.consent_status || participant?.consentStatus || "-",
          skorProtect: application?.skor_protect || undefined,
        });
      });
    }

    return {
      id: request?._id || request?.id || index,
      checked: false,
      type: isApplication ? "application" : "profile",
      title: isApplication
        ? application?.title || "Application"
        : tenantFullName || "Inquilino",
      candidates,
      applicationId: application?._id,
      skorProtect:
        application?.skor_protect || tenant?.skor_protect || undefined,
    };
  });
};

const PendingApplicationsModal = ({
  isOpen,
  onClose,
  loading,
  applications,
  workspaces,
  onRefresh,
}) => {
  const { _t } = useTranslator();
  const [selectedWorkspace, setSelectedWorkspace] = useState("");
  const [checkedApplications, setCheckedApplications] = useState(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [expandedApplications, setExpandedApplications] = useState(new Set());
  const { fetchAllLandlordNotification } = useContext(LandLordContext);

  const normalizedApplications = useMemo(
    () => normalizeApplications(applications, _t),
    [applications, _t]
  );

  const toggleExpand = (appId) => {
    setExpandedApplications((prev) => {
      const next = new Set(prev);
      if (next.has(appId)) {
        next.delete(appId);
      } else {
        next.add(appId);
      }
      return next;
    });
  };

  const handleCheckboxChange = (appId) => {
    setCheckedApplications((prev) => {
      const next = new Set(prev);
      if (next.has(appId)) {
        next.delete(appId);
      } else {
        next.add(appId);
      }
      return next;
    });
  };

  const handleSkorProtect = () => {
    window.open(
      "https://www.skortorent.com/inquilino/skor-protect",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleUpdateRequests = async (status) => {
    if (!selectedWorkspace || checkedApplications.size === 0) return;

    setIsProcessing(true);

    try {
      const promises = Array.from(checkedApplications).map((requestId) =>
        updateTenantShareRequest(requestId, {
          status,
          workspaceId: selectedWorkspace,
        })
      );

      const responses = await Promise.allSettled(promises);

      const allSuccessful = responses.every(
        (response) => response.status === "fulfilled"
      );

      if (allSuccessful) {
        await getTenantShareRequests();
        setCheckedApplications(new Set());

        const successMessage =
          status === "accepted"
            ? _t("shareRequestsAccepted") ||
              "Solicitudes aceptadas correctamente"
            : _t("shareRequestsRejected") ||
              "Solicitudes rechazadas correctamente";

        toast.success(successMessage);

        await fetchAllLandlordNotification();
        if (onRefresh) {
          onRefresh();
        }
        onClose();
      } else {
        const errorMessage =
          _t("someRequestsFailed") ||
          "Algunas solicitudes fallaron. Por favor, inténtalo de nuevo.";
        toast.error(errorMessage);
        console.error("Some requests failed");
      }
    } catch (error) {
      const errorMessage =
        _t("requestsUpdateError") ||
        "Error al actualizar las solicitudes. Por favor, inténtalo de nuevo.";
      toast.error(errorMessage);
      console.error("Error updating requests:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      className="newmodalui wrapper-modal pending-applications-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {_t("pendingApplications") || "Solicitudes pendientes"} (
          {normalizedApplications?.length || 0})
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="pending-applications-empty">
            {_t("loadingApplications") || "Cargando solicitudes..."}
          </div>
        ) : normalizedApplications.length === 0 ? (
          <div className="pending-applications-empty">
            {_t("noPendingApplications") || "No hay solicitudes pendientes."}
          </div>
        ) : (
          <div className="pending-applications-list">
            {normalizedApplications.map((application) => {
              if (application.type === "profile") {
                const candidate = application.candidates[0];

                return (
                  <div
                    key={application.id}
                    className="pending-application-card"
                  >
                    <div className="pending-application-header-row">
                      <input
                        type="checkbox"
                        checked={checkedApplications.has(application.id)}
                        onChange={() => handleCheckboxChange(application.id)}
                      />
                      <div
                        className="pending-application-avatar"
                        style={{
                          width: "24px",
                          height: "24px",
                          minWidth: "24px",
                          fontSize: "10px",
                          background: "#a3eb64",
                          color: "#253837",
                        }}
                      >
                        {candidate.initials}
                      </div>
                      <div
                        className="d-flex flex-column"
                        style={{ flex: 1, minWidth: 0, gap: "2px" }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <p
                            className="pending-application-candidate-name"
                            style={{ lineHeight: 1.2, margin: 0 }}
                          >
                            {candidate.fullName}
                          </p>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="10"
                            viewBox="0 0 8 8"
                            fill="none"
                            style={{
                              flexShrink: 0,
                              transform: "rotate(0deg)",
                            }}
                          >
                            <path
                              d="M2 1L6 4L2 7"
                              stroke="#253837"
                              strokeWidth="1.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        {candidate.consentStatus === "pending" ? (
                          <span
                            style={{
                              background: "#fef3c7",
                              color: "#d97706",
                              padding: "1px 6px",
                              borderRadius: "100px",
                              fontSize: "10px",
                              fontWeight: 600,
                              lineHeight: 1.4,
                              whiteSpace: "nowrap",
                              width: "fit-content",
                            }}
                          >
                            {_t("pending") || "Pendiente"}
                          </span>
                        ) : null}
                      </div>
                      {application.skorProtect && (
                        <>
                          <span
                            data-tooltip-id={`isf-sp-badge-tooltip-${application.id}`}
                            className="ms-2"
                          >
                            <SkorProtectBadge />
                          </span>
                          <ReactTooltip
                            id={`isf-sp-badge-tooltip-${application.id}`}
                            place="bottom"
                            className="react-tooltip-styling react-tooltip-styling-skor-protect"
                            clickable
                            style={{
                              maxWidth: "330px",
                              whiteSpace: "normal",
                            }}
                            render={() => (
                              <p
                                style={{
                                  lineHeight: 1.2,
                                }}
                              >
                                {_t("skorProtectTooltipDescription")}{" "}
                                <span
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSkorProtect();
                                  }}
                                  style={{
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                  }}
                                >
                                  {_t("skorProtectTooltipLink")}
                                </span>
                              </p>
                            )}
                          />
                        </>
                      )}
                    </div>
                    {candidate.guarantors?.length > 0 && (
                      <div className="d-flex flex-column sub-row-border-line sub-row-border-line-gurantor">
                        {candidate.guarantors.map((guarantor) => (
                          <div
                            key={guarantor.id}
                            className="guarantor-sub-row d-flex align-items-center w-100"
                          >
                            <div className="guarantor-sub-row-avatar-wrapper position-relative">
                              <img
                                src="/images/user-shield.svg"
                                alt="Guarantor"
                              />
                            </div>
                            <div className="guarantor-details-wrapper ms-2 w-100">
                              <div className="d-flex justify-content-between align-items-center w-100">
                                <div className="d-flex align-items-center gap-2">
                                  <h6 className="guarantor-name mb-0">
                                    {guarantor.fullName}
                                  </h6>
                                  <span className="guarantor-tag">
                                    {_t("guarantor", "Guarantor")}
                                  </span>
                                </div>
                              </div>
                              <div className="guarantor-income-details text-start">
                                {_t("income", "Income")}:{" "}
                                {formatIncome(
                                  guarantor.income,
                                  guarantor.isAnnualIncome
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div key={application.id} className="pending-application-card">
                  <div
                    className="pending-application-header-row"
                    onClick={() => toggleExpand(application.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={checkedApplications.has(application.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleCheckboxChange(application.id)}
                    />
                    <div className="pending-application-title-icon">
                      <div className="position-relative">
                        <div className="mobile-profile-info-box">
                          <div
                            className="userImageWrapper"
                            style={{
                              height: "24px",
                              width: "24px",
                            }}
                          >
                            <img src="/images/Flatmates.svg" alt="User Icon" />
                          </div>
                        </div>
                        <div className="member-count-wrapper d-flex align-items-center justify-content-center">
                          {application.candidates.length}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <p className="pending-application-title">
                        {application.title}
                      </p>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="10"
                        height="10"
                        viewBox="0 0 8 8"
                        fill="none"
                        style={{
                          flexShrink: 0,
                          transform: expandedApplications.has(application.id)
                            ? "rotate(90deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                        }}
                      >
                        <path
                          d="M2 1L6 4L2 7"
                          stroke="#253837"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    {application.skorProtect && (
                      <>
                        <span
                          data-tooltip-id={`isf-sp-badge-tooltip-${application.id}`}
                        >
                          <SkorProtectBadge />
                        </span>
                        <ReactTooltip
                          id={`isf-sp-badge-tooltip-${application.id}`}
                          place="bottom"
                          className="react-tooltip-styling react-tooltip-styling-skor-protect"
                          clickable
                          style={{
                            maxWidth: "330px",
                            whiteSpace: "normal",
                          }}
                          render={() => (
                            <p
                              style={{
                                lineHeight: 1.2,
                              }}
                            >
                              {_t("skorProtectTooltipDescription")}{" "}
                              <span
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSkorProtect();
                                }}
                                style={{
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                              >
                                {_t("skorProtectTooltipLink")}
                              </span>
                            </p>
                          )}
                        />
                      </>
                    )}
                  </div>

                  <div
                    className="pending-application-collapsible"
                    style={{
                      maxHeight: expandedApplications.has(application.id)
                        ? "9999px"
                        : "0",
                      opacity: expandedApplications.has(application.id) ? 1 : 0,
                      overflow: "hidden",
                      transition: "max-height 0.3s ease, opacity 0.3s ease",
                    }}
                  >
                    <div
                      className="pending-application-padding-box"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {application.candidates.map((candidate) => (
                        <div key={candidate.id}>
                          <div className="d-flex align-items-center gap-2">
                            <div
                              className="pending-application-avatar"
                              style={{
                                width: "22px",
                                height: "22px",
                                minWidth: "22px",
                                fontSize: "10px",
                                background: "#a3eb64",
                                color: "#253837",
                              }}
                            >
                              {candidate.initials}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  color: "#253837",
                                  lineHeight: 1.2,
                                }}
                              >
                                {candidate.fullName}
                              </p>
                              {candidate.consentStatus === "pending" ? (
                                <span
                                  style={{
                                    margin: 0,
                                    background: "#fef3c7",
                                    color: "#d97706",
                                    padding: "1px 6px",
                                    borderRadius: "100px",
                                    fontSize: "10px",
                                    fontWeight: 600,
                                    lineHeight: 1.4,
                                    whiteSpace: "nowrap",
                                    display: "inline-block",
                                  }}
                                >
                                  {_t("pending") || "Pendiente"}
                                </span>
                              ) : (
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: "11px",
                                    color: "#7f7f7f",
                                    lineHeight: 1.2,
                                    fontWeight: 500,
                                  }}
                                >
                                  {_t("income")}:{" "}
                                  {formatIncome(
                                    candidate.income,
                                    candidate.isAnnualIncome
                                  )}
                                </p>
                              )}
                            </div>
                          </div>
                          {candidate.guarantors?.length > 0 && (
                            <div
                              className="d-flex flex-column sub-row-border-line sub-row-border-line-gurantor"
                              style={{ margin: "4px 0 2px 11px" }}
                            >
                              {candidate.guarantors.map((guarantor) => (
                                <div
                                  key={guarantor.id}
                                  className="guarantor-sub-row d-flex align-items-center w-100"
                                >
                                  <div className="guarantor-sub-row-avatar-wrapper position-relative">
                                    <img
                                      src="/images/user-shield.svg"
                                      alt="Guarantor"
                                    />
                                  </div>
                                  <div className="guarantor-details-wrapper ms-2 w-100">
                                    <div className="d-flex justify-content-between align-items-center w-100">
                                      <div className="d-flex align-items-center gap-2">
                                        <h6 className="guarantor-name mb-0">
                                          {guarantor.fullName}
                                        </h6>
                                        <span className="guarantor-tag">
                                          {_t("guarantor", "Guarantor")}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="guarantor-income-details text-start">
                                      {_t("income", "Income")}:{" "}
                                      {formatIncome(
                                        guarantor.income,
                                        guarantor.isAnnualIncome
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="pending-application-footer-box">
          <div className="forminput customInput mb-0">
            <label>
              {_t("selectWorkspaceToSaveRequest") ||
                "¿En qué workspace quieres guardar esta solicitud?"}
            </label>
            <select
              value={selectedWorkspace}
              onChange={(e) => setSelectedWorkspace(e.target.value)}
            >
              <option value="">
                {_t("selectWorkspace") || "Selecciona un workspace..."}
              </option>
              {(workspaces || []).map((workspace) => (
                <option
                  key={workspace?._id || workspace?.uuid}
                  value={workspace?._id}
                >
                  {workspace?.name}
                </option>
              ))}
            </select>
          </div>

          <div className="pending-application-actions">
            <button
              className="pending-application-reject-btn"
              type="button"
              disabled={isProcessing || checkedApplications.size === 0}
              onClick={() => handleUpdateRequests("rejected")}
            >
              {_t("reject") || "RECHAZAR"}
            </button>
            <button
              className="pending-application-accept-btn"
              type="button"
              disabled={
                isProcessing ||
                !selectedWorkspace ||
                checkedApplications.size === 0
              }
              onClick={() => handleUpdateRequests("accepted")}
            >
              {_t("accept") || "ACEPTAR"}
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PendingApplicationsModal;
